import { Link, useRouterState } from '@tanstack/react-router';
import { useState, type PropsWithChildren } from 'react';
import { NavIcon } from '@/components/icons/NavIcon';
import { Sheet } from '@/components/ui/sheet';
import { classicGuidePages, type ClassicGuidePage } from '@/lib/classic-guide-content';
import { cn } from '@/lib/cn';

function pathIsActive(pathname: string, path: string) {
  return pathname === path || pathname.startsWith(`${path}/`);
}

const gameUrl = `${import.meta.env.VITE_GAME_URL || 'https://classic.mythea.com/'}`.trim();

type SidebarProps = {
  pathname: string;
  onNavigate?: () => void;
};

function classicGuidePath(page: ClassicGuidePage) {
  return page.id === 'introduction' ? '/classic-guide' : `/classic-guide/${page.id}`;
}

type GuideNavItem = {
  iconCode: string;
  path: string;
  title: string;
};

const classicGuidePagesById = new Map(classicGuidePages.map(page => [page.id, page] as const));

function getClassicGuideNavItem(pageId: string): GuideNavItem {
  const page = classicGuidePagesById.get(pageId);
  if (!page) {
    throw new Error(`Missing classic guide page for sidebar nav: ${pageId}`);
  }

  return {
    title: page.title,
    path: classicGuidePath(page),
    iconCode: page.iconCode,
  };
}

const primaryGuideNav: GuideNavItem[] = [
  getClassicGuideNavItem('introduction'),
  getClassicGuideNavItem('races-personalities-advisors'),
  getClassicGuideNavItem('economics-growth'),
  { title: 'Exploration', path: '/classic-guide/exploration', iconCode: 'explore' },
  getClassicGuideNavItem('science'),
  getClassicGuideNavItem('military'),
  getClassicGuideNavItem('mystics'),
  getClassicGuideNavItem('thievery'),
  getClassicGuideNavItem('combat'),
  { title: 'Dragons', path: '/classic-guide/dragons', iconCode: 'dragons' },
  getClassicGuideNavItem('nobility-honor'),
  getClassicGuideNavItem('kingdom-intelligence'),
  getClassicGuideNavItem('kingdom-monarchy'),
  getClassicGuideNavItem('sitting'),
  getClassicGuideNavItem('communication'),
  getClassicGuideNavItem('discord'),
  getClassicGuideNavItem('kingdom-relations'),
  getClassicGuideNavItem('trade-aid'),
  getClassicGuideNavItem('war-chest'),
  getClassicGuideNavItem('gems-market'),
  getClassicGuideNavItem('evolving-items'),
  getClassicGuideNavItem('servants'),
];

function SidebarNav({ pathname, onNavigate }: SidebarProps) {
  const bonusesActive = pathIsActive(pathname, '/bonuses');
  const aurasActive = pathIsActive(pathname, '/classic-guide/auras');

  return (
    <div className="space-y-4">
      <div className="glass-subtle rounded-xl p-3">
        <p className="mb-2 font-cinzel text-sm tracking-wide text-[#e6c547]">Guide</p>
        <div className="space-y-1.5">
          {primaryGuideNav.map(item => {
            const isActive =
              item.path === '/' || item.path === '/classic-guide'
                ? pathname === item.path
                : pathIsActive(pathname, item.path);

            return (
              <Link
                key={item.path}
                to={item.path as never}
                activeOptions={item.path === '/classic-guide' ? { exact: true } : undefined}
                onClick={onNavigate}
                className={cn(
                  'flex items-center gap-2 rounded-lg px-2.5 py-2 text-base transition-colors',
                  isActive
                    ? 'bg-[#d4af37]/18 text-[#f0d060]'
                    : 'text-[#d0d0d0] hover:bg-white/5 hover:text-[#f0f0f0]',
                )}
              >
                <NavIcon
                  code={item.iconCode}
                  width={24}
                  height={24}
                  className={cn(
                    'shrink-0 overflow-hidden rounded-md border object-cover',
                    isActive ? 'border-[#d4af37]/45' : 'border-white/10',
                  )}
                />
                <span className="min-w-0 truncate">{item.title}</span>
              </Link>
            );
          })}
        </div>
      </div>
      <div className="glass-subtle rounded-xl p-3">
        <p className="mb-2 font-cinzel text-sm tracking-wide text-[#e6c547]">Reference</p>
        <div className="space-y-1.5">
          <Link
            to="/bonuses"
            onClick={onNavigate}
            className={cn(
              'flex items-center gap-2 rounded-lg px-2.5 py-2 text-base transition-colors',
              bonusesActive
                ? 'bg-[#d4af37]/18 text-[#f0d060]'
                : 'text-[#d0d0d0] hover:bg-white/5 hover:text-[#f0f0f0]',
            )}
          >
            <NavIcon
              code="science"
              width={24}
              height={24}
              className={cn(
                'shrink-0 overflow-hidden rounded-md border object-cover',
                bonusesActive ? 'border-[#d4af37]/45' : 'border-white/10',
              )}
            />
            <span className="min-w-0 truncate">Bonus Reference</span>
          </Link>
          <Link
            to="/classic-guide/auras"
            onClick={onNavigate}
            className={cn(
              'flex items-center gap-2 rounded-lg px-2.5 py-2 text-base transition-colors',
              aurasActive
                ? 'bg-[#d4af37]/18 text-[#f0d060]'
                : 'text-[#d0d0d0] hover:bg-white/5 hover:text-[#f0f0f0]',
            )}
          >
            <NavIcon
              code="races"
              width={24}
              height={24}
              className={cn(
                'shrink-0 overflow-hidden rounded-md border object-cover',
                aurasActive ? 'border-[#d4af37]/45' : 'border-white/10',
              )}
            />
            <span className="min-w-0 truncate">Aura Reference</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export function GuideShell({ children }: PropsWithChildren) {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="guide-shell min-h-screen">
      <header className="glass-nav sticky top-0 z-40">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-3 py-3 sm:px-5">
          <div className="min-w-0">
            <Link to="/classic-guide" className="flex items-center gap-2">
              <img
                src="/images/nav/guide@3x.png"
                alt="Mythea guide icon"
                className="h-9 w-9 rounded-lg border border-[#d4af37]/35 object-cover"
              />
              <div className="min-w-0">
                <p className="gradient-text truncate font-cinzel text-sm tracking-[0.2em]">MYTHEA GUIDE</p>
              </div>
            </Link>
          </div>

          <div className="hidden items-center gap-2 md:flex">
            <a
              href={gameUrl}
              className="inline-flex items-center rounded-full border border-[#d4af37]/30 bg-[#d4af37]/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#e6c547] transition-colors hover:bg-[#d4af37]/18"
            >
              Back Home
            </a>
            <a
              href="https://mythea.com/"
              className="inline-flex items-center rounded-full border border-[#d4af37]/30 bg-[#d4af37]/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#e6c547] transition-colors hover:bg-[#d4af37]/18"
            >
              Main Site
            </a>
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <a
              href={gameUrl}
              className="rounded-xl border border-[#d4af37]/30 px-3 py-1.5 text-sm font-semibold text-[#e6c547]"
            >
              Home
            </a>
            <button
              className="rounded-xl border border-[#d4af37]/30 px-3 py-1.5 text-sm font-semibold text-[#e6c547]"
              onClick={() => setMobileNavOpen(true)}
            >
              Menu
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-4 px-3 pb-8 pt-4 sm:px-5 md:grid-cols-[300px_1fr]">
        <aside className="hidden md:block">
          <div className="sticky top-[86px] max-h-[calc(100vh-100px)] overflow-y-auto pr-1">
            <SidebarNav pathname={pathname} />
          </div>
        </aside>

        <main>{children}</main>
      </div>

      <Sheet isOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} title="Guide Navigation">
        <SidebarNav pathname={pathname} onNavigate={() => setMobileNavOpen(false)} />
      </Sheet>
    </div>
  );
}
